import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useBrowserLocation } from '@trinity/chat-components/hooks';
import type { Props as SelectProps } from 'react-select';
import Select from 'react-select/async';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  buildSelectStyles,
  buildSelectTheme,
} from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { useTheme } from '@trinity/chat-components/components/Theme';
import type { LocationItem } from '@trinity/chat';

type Props = {
  placeholder?: string;
  value: LocationItem;
  onChange: (value: LocationItem) => void;
} & Omit<
  SelectProps<LocationItem, false>,
  'value' | 'onChange' | 'loadOptions' | 'getOptionLabel' | 'getOptionValue'
>;

export const SelectLocation = ({
  value,
  onChange,
  placeholder = 'Start typing an address',
  styles,
  ...props
}: Props) => {
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
  const { getLocation } = useBrowserLocation({
    onSuccess: setLocation,
    onError: () => { },
  });

  const apiClient = useChatApiClient();

  const searchMutation = useMutation({
    mutationKey: ['searchLocation'],
    mutationFn: (val: string) =>
      val?.length > 1
        ? apiClient.search.searchLocation({
          query: val,
          lat: location?.latitude,
          lon: location?.longitude,
        }).then((r) => r.results)
        : Promise.resolve([] as LocationItem[]),
  });

  const onFocus = useCallback(() => {
    getLocation();
  }, [getLocation]);

  const theme = useTheme();

  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const mergedStyles = useMemo(() => {
    return Object.keys(styles || {}).reduce((acc, key) => {
      return {
        ...acc,
        [key]: (provided, state) => {
          return {
            ...(selectStyles[key]?.(provided, state) || {}),
            ...styles[key](provided, state),
          };
        },
      };
    }, selectStyles);
  }, [styles, selectStyles]);

  return (
    <Select<LocationItem>
      cacheOptions
      loadOptions={(val) => searchMutation.mutateAsync(val)}
      defaultOptions
      formatOptionLabel={({ name, poi, latitude, longitude }: LocationItem) => {
        if (poi) {
          return (
            <span>
              <span style={{ fontFamily: theme.fonts.semiBold }}>
                {poi.name}
              </span>{' '}
              ({name})
            </span>
          );
        }

        return <span>{name ?? `(${latitude}, ${longitude})`}</span>;
      }}
      getOptionValue={(option) => option.id}
      value={value}
      onFocus={onFocus}
      onChange={onChange}
      placeholder={placeholder}
      theme={selectTheme}
      styles={mergedStyles}
      {...props}
    />
  );
};
